/**
 * GESKE UK: Global Scripts
 * Handles: mega menu, mobile nav, search drawer, announcement bar,
 * product gallery, variant selector, accordions, sliders, sticky bar
 */

(function() {
  'use strict';

  /* Shared drawer helpers: the mobile nav and the search drawer are two header
     drawers that must be mutually exclusive (opening one closes the other, else
     they stack at the same z-index and the second opens hidden behind the first)
     and must expose their open state to assistive tech via aria-hidden. */
  function anyDrawerOpen() {
    return !!document.querySelector('.mobile-nav.is-open, .search-drawer.is-open, .cart-drawer.is-open');
  }
  // body.drawer-open is position:fixed (geske-cart.css), so the body must be
  // pinned at the current scroll offset and restored on close, or the page jumps
  // to the top. Pin once; ignore re-entrant locks from a second drawer.
  function lockBodyScroll() {
    if (document.body.classList.contains('drawer-open')) return;
    var y = window.scrollY || window.pageYOffset || 0;
    document.body.dataset.geskeScrollY = String(y);
    document.body.style.top = '-' + y + 'px';
    document.body.classList.add('drawer-open');
  }
  // Release and restore scroll only when no drawer remains open (the cart drawer
  // shares body.drawer-open, so don't drop the lock out from under it).
  function refreshScrollLock() {
    if (anyDrawerOpen()) return;
    if (!document.body.classList.contains('drawer-open')) return;
    document.body.classList.remove('drawer-open');
    var y = parseInt(document.body.dataset.geskeScrollY || '0', 10);
    document.body.style.top = '';
    delete document.body.dataset.geskeScrollY;
    window.scrollTo(0, y);
  }
  function drawerBackdrop() { return document.querySelector('[data-drawer-backdrop]'); }
  function showBackdrop() { var b = drawerBackdrop(); if (b) b.classList.add('is-open'); }
  function hideBackdropIfIdle() { var b = drawerBackdrop(); if (b && !anyDrawerOpen()) b.classList.remove('is-open'); }
  // Keep Tab focus inside an open drawer (accessible modal behaviour).
  function focusablesIn(c) {
    return Array.prototype.slice.call(c.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null; });
  }
  function trapTabKey(e, container) {
    if (e.key !== 'Tab') return;
    var f = focusablesIn(container);
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
  function closeMobileNav() {
    var nav = document.querySelector('.mobile-nav');
    var ham = document.querySelector('.site-header__hamburger');
    // Move focus back to the trigger before hiding, so focus is never trapped
    // inside an aria-hidden subtree.
    if (nav && ham && nav.contains(document.activeElement)) ham.focus();
    if (nav) { nav.classList.remove('is-open'); nav.setAttribute('aria-hidden', 'true'); }
    if (ham) ham.setAttribute('aria-expanded', 'false');
    refreshScrollLock();
    hideBackdropIfIdle();
  }
  function closeSearchDrawer() {
    var sd = document.querySelector('.search-drawer');
    var toggle = document.querySelector('.site-header__search-toggle');
    if (sd && toggle && sd.contains(document.activeElement)) toggle.focus();
    if (sd) { sd.classList.remove('is-open'); sd.setAttribute('aria-hidden', 'true'); }
    refreshScrollLock();
    hideBackdropIfIdle();
  }

  /* ===================== ANNOUNCEMENT BAR ===================== */
  function initAnnouncementBar() {
    var bar = document.querySelector('.announcement-bar');
    if (!bar) return;
    var header = document.querySelector('.site-header');

    // The announcement bar is in normal flow at the very top; the header is fixed.
    // Keep the header glued to the bar's bottom edge as the bar scrolls away, then
    // pin it to the top. Desktop only: on mobile the bar is a fixed bottom strip.
    function syncHeaderOffset() {
      if (!header) return;
      if (bar.style.display === 'none' || window.innerWidth <= 768) {
        header.style.top = '';
        return;
      }
      header.style.top = Math.max(0, bar.getBoundingClientRect().bottom) + 'px';
    }

    // On mobile the announcement bar is a fixed bottom strip that the Gorgias chat
    // button (fixed at bottom:20px) overlaps. Publish the bar's clearance as a CSS
    // custom property so the button can sit above it (see geske-header.css). When the
    // bar is closed or we're on desktop, drop the property and the button returns to
    // its native 20px bottom.
    function syncGorgiasOffset() {
      var root = document.documentElement;
      if (bar.style.display === 'none' || window.innerWidth > 768) {
        root.style.removeProperty('--gorgias-mobile-offset');
        return;
      }
      var clearance = window.innerHeight - bar.getBoundingClientRect().top + 12;
      root.style.setProperty('--gorgias-mobile-offset', Math.round(clearance) + 'px');
    }
    function syncOffsets() { syncHeaderOffset(); syncGorgiasOffset(); }

    var ticking = false;
    window.addEventListener('scroll', function() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function() { syncOffsets(); ticking = false; });
    }, { passive: true });
    window.addEventListener('resize', syncOffsets);
    // Re-measure once webfonts/Gorgias have settled (the bar can reflow to two lines).
    window.addEventListener('load', syncGorgiasOffset);

    var closeBtn = document.querySelector('.announcement-bar__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function() {
        bar.style.display = 'none';
        sessionStorage.setItem('geske-announcement-closed', '1');
        document.documentElement.classList.add('geske-announcement-closed');
        syncOffsets();
        // Don't strand keyboard/AT focus on the now-hidden bar; move it into the header.
        var refocus = header && header.querySelector('a, button');
        if (refocus && typeof refocus.focus === 'function') { refocus.focus(); }
      });
    }

    if (sessionStorage.getItem('geske-announcement-closed') === '1') {
      bar.style.display = 'none';
      document.documentElement.classList.add('geske-announcement-closed');
    }

    syncOffsets();
  }

  /* ===================== MOBILE NAV ===================== */
  function initMobileNav() {
    var hamburger = document.querySelector('.site-header__hamburger');
    var mobileNav = document.querySelector('.mobile-nav');
    if (!hamburger || !mobileNav) return;

    function openMobileNav() {
      closeSearchDrawer();
      mobileNav.classList.add('is-open');
      hamburger.setAttribute('aria-expanded', 'true');
      mobileNav.setAttribute('aria-hidden', 'false');
      lockBodyScroll();
      showBackdrop();
      var firstFocusable = mobileNav.querySelector('.mobile-nav__close, a, input');
      if (firstFocusable) firstFocusable.focus({ preventScroll: true });
    }

    hamburger.addEventListener('click', function() {
      if (mobileNav.classList.contains('is-open')) closeMobileNav();
      else openMobileNav();
    });

    var closeBtn = mobileNav.querySelector('.mobile-nav__close');
    if (closeBtn) closeBtn.addEventListener('click', closeMobileNav);

    // "Search" item opens the search drawer (reusing the now-hidden header toggle,
    // which also closes this nav). Falls back to its /search href without JS.
    var searchUtil = mobileNav.querySelector('[data-mobile-search]');
    if (searchUtil) searchUtil.addEventListener('click', function (e) {
      var toggle = document.querySelector('.site-header__search-toggle');
      if (toggle) {
        e.preventDefault();
        // Stop this click bubbling to the document outside-click handler, which
        // would otherwise immediately close the search drawer the toggle opens.
        e.stopPropagation();
        toggle.click();
      }
    });

    var backdrop = drawerBackdrop();
    if (backdrop) backdrop.addEventListener('click', function() { closeMobileNav(); closeSearchDrawer(); });

    // Trap Tab focus inside the open drawer.
    mobileNav.addEventListener('keydown', function(e) { trapTabKey(e, mobileNav); });

    // Close when tapping outside the open nav, and after following a link.
    document.addEventListener('click', function(e) {
      if (!mobileNav.classList.contains('is-open')) return;
      if (mobileNav.contains(e.target) || hamburger.contains(e.target)) return;
      closeMobileNav();
    });
    mobileNav.querySelectorAll('.mobile-nav__link').forEach(function(a) {
      a.addEventListener('click', closeMobileNav);
    });

    document.querySelectorAll('.mobile-nav__toggle').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.stopPropagation();
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', !expanded);
        var sub = btn.nextElementSibling;
        if (sub) sub.classList.toggle('is-open');
        btn.textContent = expanded ? '+' : '-';
      });
    });
  }

  /* ===================== SEARCH DRAWER ===================== */
  function initSearchDrawer() {
    var toggleBtn = document.querySelector('.site-header__search-toggle');
    var searchDrawer = document.querySelector('.search-drawer');
    if (!toggleBtn || !searchDrawer) return;

    toggleBtn.addEventListener('click', function() {
      var willOpen = !searchDrawer.classList.contains('is-open');
      if (willOpen) closeMobileNav();
      searchDrawer.classList.toggle('is-open', willOpen);
      searchDrawer.setAttribute('aria-hidden', willOpen ? 'false' : 'true');
      if (willOpen) {
        lockBodyScroll();
        showBackdrop();
        var input = searchDrawer.querySelector('input[type="search"]');
        if (input) input.focus();
      } else {
        refreshScrollLock();
        hideBackdropIfIdle();
      }
    });

    // Trap Tab focus inside the open search drawer.
    searchDrawer.addEventListener('keydown', function(e) { trapTabKey(e, searchDrawer); });

    // Close whichever header drawer is open on Escape (focus returns to the
    // trigger via the close helpers).
    document.addEventListener('keydown', function(e) {
      if (e.key !== 'Escape') return;
      if (document.querySelector('.mobile-nav.is-open')) closeMobileNav();
      if (document.querySelector('.search-drawer.is-open')) closeSearchDrawer();
    });

    // Close the search drawer when tapping outside it.
    document.addEventListener('click', function(e) {
      if (!searchDrawer.classList.contains('is-open')) return;
      if (searchDrawer.contains(e.target) || toggleBtn.contains(e.target)) return;
      closeSearchDrawer();
    });

    var searchInput = searchDrawer.querySelector('input[type="search"]');
    var resultsContainer = searchDrawer.querySelector('.search-drawer__results');
    var debounceTimer;

    if (searchInput && resultsContainer) {
      searchInput.addEventListener('input', function() {
        clearTimeout(debounceTimer);
        var query = searchInput.value.trim();

        if (query.length < 3) {
          resultsContainer.innerHTML = '';
          return;
        }

        debounceTimer = setTimeout(function() {
          fetch('/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6')
            .then(function(r) { return r.json(); })
            .then(function(data) {
              var products = data.resources.results.products || [];
              if (products.length === 0) {
                resultsContainer.innerHTML = '<p style="padding: 16px; color: var(--color-text-muted);">No results found</p>';
                return;
              }
              var html = products.map(function(p) {
                var img = p.image ? p.image.replace(/(\.[^.]+)$/, '_120x120$1') : '';
                return '<a href="' + p.url + '" class="search-result" style="display:flex;gap:12px;padding:8px 0;border-bottom:1px solid var(--color-border-light);align-items:center;">' +
                  '<img src="' + img + '" alt="" width="48" height="48" style="border-radius:4px;object-fit:cover;background:var(--color-bg-secondary);">' +
                  '<div>' +
                    '<strong style="font-size:14px;display:block;">' + p.title + '</strong>' +
                    '<span style="font-size:13px;color:var(--color-text-muted);">' + p.price + '</span>' +
                  '</div>' +
                '</a>';
              }).join('');
              resultsContainer.innerHTML = html;
            })
            .catch(function() {
              resultsContainer.innerHTML = '';
            });
        }, 300);
      });
    }
  }

  /* ===================== PRODUCT GALLERY ===================== */
  // On 3D PDPs the brown hero glow is tuned for the device render. Flat photos
  // reached via the thumbs read badly on the brown, so swap to a neutral wash
  // (and dark ink) whenever the active media is not the model. Gated by
  // data-has-model so non-3D PDPs are untouched. CSS owns the transition.
  function syncGalleryBackdrop(activeItem) {
    var main = document.querySelector('.product-main');
    if (!main || main.dataset.hasModel !== 'true' || !activeItem) return;
    main.classList.toggle('is-neutral', activeItem.dataset.mediaType !== 'model');
  }

  function initProductGallery() {
    var gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;

    var items = gallery.querySelectorAll('.product-gallery__item');
    var thumbs = document.querySelectorAll('.product-gallery__thumb');

    thumbs.forEach(function(thumb) {
      thumb.addEventListener('click', function() {
        var targetId = thumb.dataset.thumbTarget;
        var activated = null;

        items.forEach(function(item) {
          item.classList.remove('is-active');
          if (item.dataset.mediaId === targetId) {
            item.classList.add('is-active');
            activated = item;
          }
        });

        thumbs.forEach(function(t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');

        syncGalleryBackdrop(activated);
      });
    });

    syncGalleryBackdrop(gallery.querySelector('.product-gallery__item.is-active'));
  }

  /* ===================== VARIANT SELECTOR ===================== */
  function initVariantSelector() {
    var selector = document.querySelector('[data-variant-selector]');
    if (!selector) return;

    var form = document.querySelector('[data-product-form]');
    var variantIdInput = form ? form.querySelector('[data-variant-id]') : null;

    selector.addEventListener('change', function(e) {
      if (!e.target.matches('input[type="radio"]')) return;

      var selectedValue = e.target.value;

      // Update swatch active states
      selector.querySelectorAll('.colour-swatch').forEach(function(swatch) {
        swatch.classList.remove('is-selected');
        if (swatch.dataset.swatchValue === selectedValue) {
          swatch.classList.add('is-selected');
        }
      });

      // Update colour label value
      var colourLabel = document.querySelector('.variant-colour-label__value');
      if (colourLabel) colourLabel.textContent = selectedValue;

      // Update sticky bar colour select
      var stickySelect = document.querySelector('[data-sticky-colour-select]');
      if (stickySelect) stickySelect.value = selectedValue;

      // Find matching variant
      if (window.geskeProductVariants) {
        var variant = window.geskeProductVariants.find(function(v) {
          return v.option1 === selectedValue || v.option2 === selectedValue;
        });

        if (variant) {
          // Update hidden variant ID in all forms
          document.querySelectorAll('[data-variant-id]').forEach(function(input) {
            input.value = variant.id;
          });

          // Update URL
          var url = new URL(window.location);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());

          // Update price
          var priceEl = document.querySelector('.product-main__price-row .price');
          if (priceEl && variant.price) {
            priceEl.textContent = '£' + (variant.price / 100).toFixed(2);
          }

          // Update buy/notify state
          var formContainer = document.querySelector('.product-main__form');
          var notifyContainer = document.querySelector('.product-main__notify');

          if (variant.available) {
            if (formContainer) formContainer.style.display = '';
            if (notifyContainer) notifyContainer.style.display = 'none';
          } else {
            if (formContainer) formContainer.style.display = 'none';
            if (notifyContainer) notifyContainer.style.display = '';
          }

          // Update gallery images based on variant
          updateGalleryForVariant(variant);
        }
      }
    });
  }

  function updateGalleryForVariant(variant) {
    var gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;

    var items = gallery.querySelectorAll('.product-gallery__item');
    var firstMatch = null;

    items.forEach(function(item) {
      var variantIds = item.dataset.variantIds;
      if (variantIds && variantIds.includes(String(variant.id))) {
        if (!firstMatch) firstMatch = item;
      }
    });

    if (firstMatch) {
      items.forEach(function(item) { item.classList.remove('is-active'); });
      firstMatch.classList.add('is-active');

      var thumbs = document.querySelectorAll('.product-gallery__thumb');
      thumbs.forEach(function(t) {
        t.classList.remove('is-active');
        if (t.dataset.thumbTarget === firstMatch.dataset.mediaId) {
          t.classList.add('is-active');
        }
      });

      syncGalleryBackdrop(firstMatch);
    }
  }

  /* ===================== PRODUCT ACCORDIONS ===================== */
  function initAccordions() {
    document.querySelectorAll('.product-accordion__header, .faq-item__question').forEach(function(header) {
      header.addEventListener('click', function() {
        var accordion = header.closest('.product-accordion, .faq-item');
        if (!accordion) return;

        var isOpen = accordion.classList.contains('is-open');

        // Close siblings if single-open mode
        var parent = accordion.parentElement;
        if (parent && parent.dataset.singleOpen === 'true') {
          parent.querySelectorAll('.product-accordion, .faq-item').forEach(function(sibling) {
            sibling.classList.remove('is-open');
            var siblingHeader = sibling.querySelector('.product-accordion__header, .faq-item__question');
            if (siblingHeader) siblingHeader.setAttribute('aria-expanded', 'false');
          });
        }

        var nextOpen = !isOpen;
        accordion.classList.toggle('is-open', nextOpen);
        header.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
      });
    });
  }

  /* ===================== HOW-TO-USE SLIDER ===================== */
  function initHowToUseSlider() {
    var container = document.querySelector('[data-how-to-use-track]');
    if (!container) return;

    var track = container.querySelector('.product-how-to-use__steps-track');
    var steps = container.querySelectorAll('.how-to-use-step');
    var prevBtn = document.querySelector('.how-to-use-slider__nav [data-slider-prev]');
    var nextBtn = document.querySelector('.how-to-use-slider__nav [data-slider-next]');
    var counter = document.querySelector('.how-to-use-slider__nav [data-slider-counter]');

    // Cards are flex 0 0 40% (~48% on tablet, ~80% on mobile), so anything past
    // 2 cards already overflows the visible row on every breakpoint. Bailing at
    // <= 3 (matching the old "only show nav past 3 steps" Liquid condition) left
    // exactly-3-step PDPs (e.g. Facial Brush 4 in 1) with a 3rd card rendered but
    // permanently clipped by `overflow: hidden` and no nav/swipe to reach it.
    if (!track || steps.length <= 2) return;

    // Advance one card at a time, .com-style, with a peek of the next card. At the
    // end, stop with END_GUTTER of breathing room to the right of the last card
    // instead of pinning it flush to the container edge.
    var currentIndex = 0;
    var END_GUTTER = 32;

    function metrics() {
      var stepWidth = steps[0].offsetWidth;
      var gap = parseInt(getComputedStyle(track).gap) || 0;
      var pitch = stepWidth + gap;
      var viewport = container.clientWidth;
      var visibleFull = Math.max(1, Math.floor((viewport + gap) / pitch + 0.02));
      return {
        pitch: pitch,
        viewport: viewport,
        trackWidth: track.scrollWidth,
        maxIndex: Math.max(0, steps.length - visibleFull)
      };
    }

    function goTo(index) {
      var m = metrics();
      if (index < 0) index = 0;
      if (index > m.maxIndex) index = m.maxIndex;
      currentIndex = index;
      var maxScroll = Math.max(0, m.trackWidth - m.viewport);
      var endStop = maxScroll > 0 ? maxScroll + END_GUTTER : 0;
      var offset = Math.min(currentIndex * m.pitch, endStop);
      track.style.transform = 'translateX(-' + offset + 'px)';
      if (counter) counter.textContent = (currentIndex + 1) + '/' + (m.maxIndex + 1);
      if (prevBtn) prevBtn.disabled = currentIndex === 0;
      if (nextBtn) nextBtn.disabled = currentIndex >= m.maxIndex;
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function() { goTo(currentIndex - 1); });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function() { goTo(currentIndex + 1); });
    }
    window.addEventListener('resize', function() { goTo(currentIndex); });

    // Touch swipe: advance one card per horizontal swipe. The cards deliberately
    // peek the next one to invite a swipe, but previously only the arrows worked.
    var swipeStartX = null, swipeStartY = null;
    container.addEventListener('touchstart', function(e) {
      if (e.touches.length !== 1) { swipeStartX = null; return; }
      swipeStartX = e.touches[0].clientX;
      swipeStartY = e.touches[0].clientY;
    }, { passive: true });
    container.addEventListener('touchend', function(e) {
      if (swipeStartX === null) return;
      var t = e.changedTouches[0];
      var dx = t.clientX - swipeStartX, dy = t.clientY - swipeStartY;
      swipeStartX = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return; // ignore vertical scroll
      goTo(currentIndex + (dx < 0 ? 1 : -1));
    }, { passive: true });

    goTo(0);
  }

  /* ===================== TECHNOLOGY TABS ===================== */
  function initTechnologyTabs() {
    var tabs = document.querySelectorAll('[data-tech-tab]');
    var images = document.querySelectorAll('[data-tech-image]');
    var indicators = document.querySelectorAll('[data-tech-indicator]');
    if (!tabs.length) return;

    function activate(index) {
      tabs.forEach(function(t) {
        var match = t.dataset.techTab === index;
        t.classList.toggle('is-active', match);
        t.setAttribute('aria-pressed', match ? 'true' : 'false');
      });
      images.forEach(function(img) {
        img.classList.toggle('is-active', img.dataset.techImage === index);
      });
      indicators.forEach(function(ind) {
        var match = ind.dataset.techIndicator === index;
        ind.classList.toggle('is-active', match);
        ind.setAttribute('aria-pressed', match ? 'true' : 'false');
      });
    }

    tabs.forEach(function(tab) {
      tab.addEventListener('click', function() { activate(tab.dataset.techTab); });
    });
    indicators.forEach(function(ind) {
      ind.addEventListener('click', function() { activate(ind.dataset.techIndicator); });
    });
  }

  /* ===================== HERO SLIDESHOW ===================== */
  function initHeroSlideshow() {
    var slideshow = document.querySelector('.hero-slideshow');
    if (!slideshow) return;

    var slides = slideshow.querySelectorAll('.hero-slideshow__slide');
    var dots = slideshow.querySelectorAll('.hero-slideshow__dot');
    if (slides.length <= 1) return;

    var currentSlide = 0;
    var autoplaySpeed = parseInt(slideshow.dataset.autoplaySpeed) || 5000;
    var autoplay = slideshow.dataset.autoplay !== 'false';
    var interval;

    function goToSlide(index) {
      slides.forEach(function(s) { s.classList.remove('is-active'); });
      dots.forEach(function(d) { d.classList.remove('is-active'); });
      slides[index].classList.add('is-active');
      if (dots[index]) dots[index].classList.add('is-active');
      currentSlide = index;
    }

    function nextSlide() {
      goToSlide((currentSlide + 1) % slides.length);
    }

    dots.forEach(function(dot, index) {
      dot.addEventListener('click', function() {
        goToSlide(index);
        if (interval) clearInterval(interval);
        if (autoplay) interval = setInterval(nextSlide, autoplaySpeed);
      });
    });

    if (autoplay) {
      interval = setInterval(nextSlide, autoplaySpeed);
    }
  }

  /* ===================== AWARDS CAROUSEL ===================== */
  function initAwardsCarousel() {
    var track = document.querySelector('.awards-carousel__track');
    if (!track) return;

    // Duplicate items for seamless loop
    var items = track.innerHTML;
    track.innerHTML = items + items;
  }

  /* ===================== STICKY PRODUCT BAR ===================== */
  function initStickyProductBar() {
    var bar = document.querySelector('.sticky-product-bar');
    var buyBtn = document.querySelector('.product-main__buy-btn') || document.querySelector('.product-main__notify');
    if (!bar || !buyBtn) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        var visible = !entry.isIntersecting;
        bar.classList.toggle('is-visible', visible);
        // Mirror onto <body> so the fixed header pill can hide while the sticky
        // bar covers the top of the viewport (desktop overlap). See
        // geske-header.css body.sticky-bar-shown rule.
        document.body.classList.toggle('sticky-bar-shown', visible);
      });
    }, { threshold: 0 });

    observer.observe(buyBtn);
  }

  /* ===================== AWARDS ENHANCEMENT ===================== */
  function initAwardsEnhancement() {
    var awards = document.querySelector('.awards-carousel, .awards-section');
    if (!awards) return;

    // Inject floating product image if not already present
    if (!awards.querySelector('.awards-section__product-left')) {
      // Find the product image asset URL from any existing theme asset
      var testImg = document.querySelector('img[src*="callout-sonic-brush-white"]');
      var imgSrc = testImg ? testImg.src : '';

      // Try to construct the URL from the CDN pattern
      if (!imgSrc) {
        var anyAsset = document.querySelector('link[href*="assets/geske-"]');
        if (anyAsset) {
          var base = anyAsset.href.split('assets/')[0] + 'assets/';
          imgSrc = base + 'callout-sonic-brush-white.png';
        }
      }

      if (imgSrc) {
        var productDiv = document.createElement('div');
        productDiv.className = 'awards-section__product-left';
        productDiv.innerHTML = '<img src="' + imgSrc + '" alt="" loading="lazy">';
        productDiv.style.cssText = 'position:absolute;left:-5%;top:50%;transform:translateY(-55%);width:45%;max-width:600px;z-index:1;pointer-events:none;opacity:0.55;';
        awards.appendChild(productDiv);
      }
    }

    // Inject extra text after description if not present
    var desc = awards.querySelector('.awards-carousel__description, .awards-section__description');
    if (desc && !awards.querySelector('.awards-section__extra, .awards-carousel__extra')) {
      var extra = document.createElement('p');
      extra.className = 'awards-section__extra';
      extra.style.cssText = 'font-size:15px;color:#50555e;line-height:1.6;margin-bottom:8px;font-style:italic;text-align:center;';
      extra.textContent = 'Take the plunge and discover for yourself what makes our devices so incredibly special.';
      desc.after(extra);

      var tagline = document.createElement('p');
      tagline.className = 'awards-section__tagline';
      tagline.style.cssText = 'font-size:15px;font-weight:600;color:#141921;text-align:center;margin-bottom:40px;';
      tagline.textContent = 'Reimagine your skin with GESKE!';
      extra.after(tagline);
    }
  }

  /* ===================== STICKY BAR COLOUR SELECT ===================== */
  function initStickyBarColourSelect() {
    var select = document.querySelector('[data-sticky-colour-select]');
    if (!select) return;

    select.addEventListener('change', function() {
      var value = select.value;
      // Trigger the matching swatch radio in the main variant selector
      var swatch = document.querySelector('.colour-swatch[data-swatch-value="' + value + '"] input');
      if (swatch) {
        swatch.checked = true;
        swatch.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /* ===================== SCROLL ANIMATIONS ===================== */
  function initScrollAnimations() {
    var animatedEls = document.querySelectorAll('.animate-on-scroll, .animate-from-left, .animate-from-right, .animate-scale');
    if (!animatedEls.length || !('IntersectionObserver' in window)) {
      // No animation support or no elements: show everything
      animatedEls.forEach(function(el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -50px 0px' });

    animatedEls.forEach(function(el) { observer.observe(el); });
  }

  /* ===================== GORGIAS CHAT: HIDE ON SCROLL (PDP) =====================
     On the PDP, hide the Gorgias live-chat widget once the user begins scrolling
     down, and reveal it again at the top. We toggle a body class (CSS in
     geske-product.css does the hiding) so the rule survives Gorgias re-mounting
     its container. */
  function initGorgiasScrollHide() {
    if (!document.body.classList.contains('template-product')) return;
    var THRESHOLD = 60; // px scrolled before the chat hides
    var ticking = false;
    function update() {
      var y = window.scrollY || window.pageYOffset || 0;
      document.body.classList.toggle('pdp-chat-hidden', y > THRESHOLD);
      ticking = false;
    }
    window.addEventListener('scroll', function() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    update(); // set initial state (e.g. page loaded already scrolled)
  }

  /* ===================== INIT ===================== */
  function init() {
    initScrollAnimations();
    initAnnouncementBar();
    initMobileNav();
    initSearchDrawer();
    initProductGallery();
    initVariantSelector();
    initAccordions();
    initHowToUseSlider();
    initTechnologyTabs();
    initStickyBarColourSelect();
    initHeroSlideshow();
    initAwardsCarousel();
    initAwardsEnhancement();
    initStickyProductBar();
    initGorgiasScrollHide();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
